import { Component, inject, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FatService, FatFieldDto, FatFieldDocumentDto } from '../../../../../core/services/fat.service';
import { ConfirmDialogComponent } from '../../../../../shared/components/confirm-dialog.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';


export interface FatDocumentsDialogData {
  projectId: number;
  field: FatFieldDto;
  readonly?: boolean;
}

@Component({
  selector: 'app-fat-documents-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ConfirmDialogComponent,
    MatProgressBarModule
  ],

  templateUrl: './fat-documents-dialog.component.html',
  styleUrl: './fat-documents-dialog.component.scss'
})
export class FatDocumentsDialogComponent {
  private fatService = inject(FatService);
  private data: FatDocumentsDialogData = inject(MAT_DIALOG_DATA);

  field = signal<FatFieldDto>(this.data.field);
  projectId = this.data.projectId;
  isReadonly = signal(this.data.readonly);

  showDeleteConfirm = signal(false);
  documentPendingDelete = signal<FatFieldDocumentDto | null>(null);
  isDragging = signal(false);
  isUploading = signal(false);


  constructor(public dialogRef: MatDialogRef<FatDocumentsDialogComponent>) {}

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.isReadonly()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFilesSelected(event: Event): void {
    if (this.isReadonly()) return;
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.handleFiles(Array.from(input.files));
    input.value = '';
  }

  private handleFiles(files: File[]): void {
    if (files.length === 0) return;

    this.isUploading.set(true);

    const uploads = files.map(file =>
      this.fatService.uploadDocument(this.projectId, this.field().id, file).pipe(
        tap(doc => {
          const currentField = this.field();
          if (!currentField.documents) currentField.documents = [];
          currentField.documents.push(doc);
          this.field.set({ ...currentField });
        }),
        catchError(err => {
          console.error(`Erreur lors de l'upload du document`, err);
          return of(null);
        })
      )
    );

    forkJoin(uploads).pipe(
      finalize(() => this.isUploading.set(false))
    ).subscribe();
  }

  askDeleteDocument(doc: FatFieldDocumentDto): void {
    this.documentPendingDelete.set(doc);
    this.showDeleteConfirm.set(true);
  }

  confirmDeleteDocument(): void {
    const doc = this.documentPendingDelete();
    if (!doc) return;

    this.fatService.deleteDocument(this.projectId, doc.id).subscribe({
      next: () => {
        const currentField = this.field();
        currentField.documents = (currentField.documents || []).filter(d => d.id !== doc.id);
        this.field.set({ ...currentField });
        this.cancelDeleteDocument();
      },
      error: () => {
        console.error('Erreur lors de la suppression du document');
        this.cancelDeleteDocument();
      }
    });
  }

  cancelDeleteDocument(): void {
    this.showDeleteConfirm.set(false);
    this.documentPendingDelete.set(null);
  }

  downloadDocument(doc: FatFieldDocumentDto): void {
    this.fatService.getDocumentDownload(this.projectId, doc.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: (err) => console.error('Erreur lors du téléchargement du document:', err)
    });
  }

  getFileIcon(contentType: string | null): string {
    if (!contentType) return 'insert_drive_file';
    if (contentType.includes('pdf')) return 'picture_as_pdf';
    if (contentType.includes('word') || contentType.includes('document')) return 'description';
    if (contentType.includes('excel') || contentType.includes('sheet')) return 'table_chart';
    if (contentType.includes('image')) return 'image';
    if (contentType.includes('zip') || contentType.includes('archive')) return 'folder_zip';
    return 'insert_drive_file';
  }

  formatFileSize(size: number | null): string {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  }

  close(): void {
    this.dialogRef.close(this.field());
  }
}

